import { apiClient } from '../client';
import type { ApiResponse } from '../types';

// ---------------------------------------------------------------------------
// Which build clients should be running.
//
// iTow is an installed PWA, so a browser keeps serving the shell it already
// has. Every deploy this project has shipped needed someone to be told "hard
// refresh", and more than one bug was chased twice because the first fix was
// already live and invisible. This is how a running tab finds out.
// ---------------------------------------------------------------------------

export interface AppVersion {
  /**
   * Opaque token. Compared for equality and nothing else — it is not a
   * semantic version, and parsing or ordering it will eventually be wrong.
   */
  version: string;
  /** The API's own deployment identity — the Cloud Run revision name. */
  serverBuild: string;
  /** Optional line for the reload prompt. Null renders the generic copy. */
  message: string | null;
  /** Reload without asking. Reserved for a build that is actively broken. */
  forceReload: boolean;
  /** When an administrator last bumped. Null if never. */
  publishedAt: string | null;
}

/**
 * GET /api/v1/app-version
 *
 * Anonymous — a stale login screen is as much of a problem as a stale
 * dashboard, and a client running last month's bundle against a changed auth
 * contract cannot sign in to be told anything.
 */
export async function getAppVersion(): Promise<AppVersion> {
  const response = await apiClient.get<ApiResponse<AppVersion>>('/api/v1/app-version');
  return response.data.data;
}

/**
 * POST /api/v1/app-version/bump — administrators only.
 *
 * Asks every open tab in the product to reload. `version` is optional; left
 * blank the server stamps the current time, which is what "make everyone
 * reload now" means.
 */
export async function bumpAppVersion(payload: {
  version?: string;
  message?: string;
  forceReload?: boolean;
}): Promise<AppVersion> {
  const response = await apiClient.post<ApiResponse<AppVersion>>(
    '/api/v1/app-version/bump',
    payload,
  );
  return response.data.data;
}
